import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, FormHelperText, Snackbar, Alert,
  Checkbox, CircularProgress, Chip, FormControlLabel,
  Backdrop, Autocomplete
} from '@mui/material';
import { Close, Payment, Warning, Print, CheckCircle } from '@mui/icons-material';
import { fetchData } from '../../../apiService';
import { useMobileSpacing } from '../../../utils/mobileSpacing';
import { formatDate } from '../../../utils/dateUtils';

// Theme config for dynamic colors
const themeConfig = {
  payment: { primary: '#dc6363', secondary: '#b91c1c', light: 'rgba(220,99,99,0.1)', background: 'rgba(220,99,99,0.05)' },
  receipt: { primary: '#429178', secondary: '#047857', light: 'rgba(66,145,120,0.1)', background: 'rgba(66,145,120,0.05)' }
};

const PaymentReceiptsModal = ({
  open, onClose, modalAction, formData, setFormData,
  paymentReceiptDetails, setPaymentReceiptDetails, onSave,
  masters, banks, purchases, sales, paymentMethods, gstRates,
  loading, companyId, token, fetchPurchases, fetchSales,
  fetchBillsForMaster, fetchMaxBillNo, branchId, fromDate, toDate, onPrint
}) => {
  const theme = useTheme();
  const mobileSpacing = useMobileSpacing();
  const isMobile = mobileSpacing.isMobile;

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const amountChangedFromBillsRef = useRef(false);
  const lastFetchedMasterIdRef = useRef(null);

  const isViewMode = modalAction === 'view';
  const transactionType = formData.IsType ? 'payment' : 'receipt';

  // Dynamically resolve the cheque payment method ID by name
  const getChequePaymentMethodId = () => {
    if (paymentMethods && paymentMethods.length > 0) {
      const chequeMethod = paymentMethods.find(pm => pm.Name && pm.Name.toLowerCase().includes('cheque'));
      return chequeMethod ? chequeMethod.Id : 2;
    }
    return 2; // fallback
  };
  const chequePaymentMethodId = getChequePaymentMethodId();

  // Returns true when the selected payment method requires bank details
  const isBankDetailsRequired = () => {
    if (!formData.PaymentType) return false;
    const cashMethod = paymentMethods && paymentMethods.length > 0
      ? paymentMethods.find(pm => pm.Name && pm.Name.toLowerCase().includes('cash'))
      : null;
    const cashId = cashMethod ? cashMethod.Id : 1;
    return parseInt(formData.PaymentType) !== cashId;
  };
  const currentTheme = transactionType === 'payment' ? themeConfig.payment : themeConfig.receipt;

  const headerColor = isViewMode ? '#1e293b' : (transactionType === 'payment' ? '#fdf2f2' : '#f0fdf4');
  const headerBorderColor = isViewMode ? '#e2e8f0' : (transactionType === 'payment' ? '#fecaca' : '#d1fae5');
  const headerTextColor = isViewMode ? '#f8fafc' : (transactionType === 'payment' ? '#7f1d1d' : '#064e3b');
  const iconColor = isViewMode ? '#f8fafc' : currentTheme.primary;
  const contentBg = isViewMode ? '#f8fafc' : '#fefffe';
  const actionsBg = isViewMode ? '#f8fafc' : (transactionType === 'payment' ? '#fdf2f2' : '#f0fdf4');
  const actionsBorder = isViewMode ? '1px solid #e2e8f0' : (transactionType === 'payment' ? '1px solid #fecaca' : '1px solid #d1fae5');
  const sectionBg = isViewMode ? '#f1f5f9' : (transactionType === 'payment' ? '#fdf2f2' : '#f0fdf4');

  const compactInput = {
    '& .MuiInputBase-root': { height: '28px' },
    '& .MuiInputBase-input': { py: 0, fontSize: '0.75rem' },
    '& .MuiInputLabel-root': { fontSize: '0.75rem', lineHeight: '1rem' },
    '& .MuiSelect-select': { py: 0, fontSize: '0.75rem' }
  };

  const getTypeBasedContent = () => {
    const isPayment = formData.IsType;
    if (modalAction === 'create') return {
      title: isPayment ? 'Create Payment' : 'Create Receipt',
      infoCaption: isPayment ? 'Payment Information' : 'Receipt Information',
      billNoLabel: isPayment ? 'Voucher No *' : 'Receipt No *',
      billDateLabel: isPayment ? 'Voucher Date *' : 'Receipt Date *'
    };
    if (modalAction === 'edit') return {
      title: isPayment ? 'Edit Payment' : 'Edit Receipt',
      infoCaption: isPayment ? 'Payment Information' : 'Receipt Information',
      billNoLabel: isPayment ? 'Voucher No *' : 'Receipt No *',
      billDateLabel: isPayment ? 'Voucher Date *' : 'Receipt Date *'
    };
    return {
      title: isPayment ? 'View Payment' : 'View Receipt',
      infoCaption: isPayment ? 'Payment Information' : 'Receipt Information',
      billNoLabel: isPayment ? 'Voucher No' : 'Receipt No',
      billDateLabel: isPayment ? 'Voucher Date' : 'Receipt Date'
    };
  };

  const typeContent = getTypeBasedContent();

  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setCancelConfirmOpen(false);
      amountChangedFromBillsRef.current = false;
      lastFetchedMasterIdRef.current = null;
      setModalLoading(true);
      const timer = setTimeout(() => setModalLoading(false), 300);
      return () => clearTimeout(timer);
    } else {
      setModalLoading(false);
    }
  }, [open]);

  useEffect(() => {}, [formData.IsType]);

  const distributeAmountAcrossBills = useCallback((totalAmount) => {
    if (!paymentReceiptDetails || paymentReceiptDetails.length === 0 || paymentReceiptDetails[0]?.noData) return;
    const updatedDetails = [...paymentReceiptDetails];
    let remainingAmount = parseFloat(totalAmount) || 0;
    for (let i = 0; i < updatedDetails.length; i++) {
      updatedDetails[i].IsSelected = false;
      updatedDetails[i].PaymentAmount = 0;
    }
    if (remainingAmount <= 0) { setPaymentReceiptDetails(updatedDetails); return; }
    const totalPendingAmount = updatedDetails.reduce((sum, d) => sum + (d.PendingAmount || 0), 0);
    if (remainingAmount >= totalPendingAmount) {
      for (let i = 0; i < updatedDetails.length; i++) {
        updatedDetails[i].IsSelected = true;
        updatedDetails[i].PaymentAmount = updatedDetails[i].PendingAmount || 0;
      }
    } else {
      for (let i = 0; i < updatedDetails.length && remainingAmount > 0; i++) {
        const pendingAmount = updatedDetails[i].PendingAmount || 0;
        if (remainingAmount >= pendingAmount) {
          updatedDetails[i].IsSelected = true;
          updatedDetails[i].PaymentAmount = pendingAmount;
          remainingAmount -= pendingAmount;
        } else {
          updatedDetails[i].IsSelected = true;
          updatedDetails[i].PaymentAmount = remainingAmount;
          remainingAmount = 0;
        }
      }
    }
    setPaymentReceiptDetails(updatedDetails);
  }, [paymentReceiptDetails]);

  const calculateTotals = useCallback(() => {
    const amount = parseFloat(formData.Amount) || 0;
    const roundOff = Math.round(amount) - amount;
    setFormData(prev => ({ ...prev, RoundOff: Math.round(roundOff * 100) / 100, TotalAmount: Math.round(amount) }));
  }, [formData.Amount]);

  const calculateGSTAmounts = useCallback(() => {
    const amount = parseFloat(formData.Amount) || 0;
    const gstRate = parseFloat(formData.GSTRate) || 0;
    if (amount > 0 && gstRate > 0 && formData.IsGST) {
      const totalGstAmount = (amount * gstRate) / 100;
      if (formData.IsIGST) {
        setFormData(prev => ({ ...prev, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: Math.round(totalGstAmount * 100) / 100, TotalAmount: Math.round((amount + totalGstAmount) * 100) / 100 }));
      } else {
        const half = totalGstAmount / 2;
        setFormData(prev => ({ ...prev, CGSTAmount: Math.round(half * 100) / 100, SGSTAmount: Math.round(half * 100) / 100, IGSTAmount: 0, TotalAmount: Math.round((amount + totalGstAmount) * 100) / 100 }));
      }
    } else {
      setFormData(prev => ({ ...prev, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0 }));
      calculateTotals();
    }
  }, [formData.Amount, formData.GSTRate, formData.IsGST, formData.IsIGST]);

  useEffect(() => {
    if (modalAction === 'create' && formData.MasterId && masters.length > 0) {
      const selectedMaster = masters.find(m => m.Id === parseInt(formData.MasterId));
      if (selectedMaster) {
        if (formData.IsType && purchases.length > 0) {
          const purchaseDetails = purchases.map(p => ({ Id: p.PurchaseId, Description: p.PurchaseDescription, Amount: p.PurchaseAmount, PaidAmount: p.PaidAmount, BalanceAmount: p.BalanceAmount, PendingAmount: p.PendingAmount, BillDate: p.BillDate, Type: 'Purchase', PaymentAmount: 0, IsSelected: false }));
          setPaymentReceiptDetails(purchaseDetails);
          const totalPending = purchaseDetails.reduce((sum, p) => sum + (p.PendingAmount || 0), 0);
          setFormData(prev => ({ ...prev, Amount: totalPending, TotalAmount: totalPending }));
        } else if (!formData.IsType && sales.length > 0) {
          const codSaleId = formData._codSaleId ? parseInt(formData._codSaleId) : null;
          const saleDetails = sales.map(s => {
            const isMatch = codSaleId !== null && parseInt(s.SaleId) === codSaleId;
            return {
              Id: s.SaleId, Description: s.SaleDescription, Amount: s.SaleAmount,
              PaidAmount: s.PaidAmount, BalanceAmount: s.BalanceAmount, PendingAmount: s.PendingAmount,
              BillDate: s.BillDate, Type: 'Sale',
              PaymentAmount: isMatch ? (s.PendingAmount || s.SaleAmount || 0) : 0,
              IsSelected: isMatch
            };
          });
          setPaymentReceiptDetails(saleDetails);
          const totalPending = saleDetails.reduce((sum, s) => sum + (s.PendingAmount || 0), 0);
          // If a specific COD sale is pre-selected, default amount to that sale's pending; otherwise use total
          const matchedSale = codSaleId !== null ? saleDetails.find(d => parseInt(d.Id) === codSaleId) : null;
          const defaultAmount = matchedSale ? (matchedSale.PendingAmount || matchedSale.Amount || totalPending) : totalPending;
          // Prevent the distributeAmountAcrossBills effect from overwriting our specific selection
          if (matchedSale) amountChangedFromBillsRef.current = true;
          setFormData(prev => ({ ...prev, Amount: defaultAmount, TotalAmount: defaultAmount }));
        }
        if (purchases.length === 0 && sales.length === 0) {
          const timer = setTimeout(() => {
            if (paymentReceiptDetails.length === 0) setPaymentReceiptDetails([{ noData: true }]);
          }, 2000);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [formData.MasterId, formData.IsType, formData._codSaleId, purchases, sales, masters, modalAction]);

  useEffect(() => {
    if (!isViewMode && paymentReceiptDetails.length > 0 && formData.Amount && !paymentReceiptDetails[0]?.noData) {
      // Skip redistribution if amount was updated from manual bill selection/deselection
      if (amountChangedFromBillsRef.current) {
        amountChangedFromBillsRef.current = false;
        return;
      }
      const id = setTimeout(() => distributeAmountAcrossBills(formData.Amount), 150);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentReceiptDetails.length, formData.Amount, isViewMode]);

  useEffect(() => {
    if (formData.IsGST && formData.Amount && formData.GSTRate) {
      const id = setTimeout(() => calculateGSTAmounts(), 100);
      return () => clearTimeout(id);
    }
  }, [formData.Amount, formData.GSTRate, formData.IsGST, formData.IsIGST]);

  const showError = (msg) => setSnackbar({ open: true, message: msg, severity: 'error' });
  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  const handleCancelClick = () => {
    if (hasUnsavedChanges && !isViewMode) setCancelConfirmOpen(true);
    else onClose();
  };
  const handleConfirmCancel = () => { setCancelConfirmOpen(false); setHasUnsavedChanges(false); onClose(); };
  const handleStayOnPage = () => setCancelConfirmOpen(false);

  const validateField = (field, value) => {
    const tc = getTypeBasedContent();
    switch (field) {
      case 'BillNumber': return (!value || value.toString().trim() === '') ? `${tc.billNoLabel.replace(' *', '')} is required` : '';
      case 'BillDate': return !value ? `${tc.billDateLabel.replace(' *', '')} is required` : '';
      case 'MasterId': return !value ? 'Master is required' : '';
      case 'Amount': return (!value || parseFloat(value) <= 0) ? 'Amount must be greater than 0' : '';
      case 'ChequeNumber': return isBankDetailsRequired() && (!value || value.toString().trim() === '') ? 'Reference Number is required' : '';
      case 'ChequeDate': return isBankDetailsRequired() && !value ? 'Transaction Date is required' : '';
      case 'BankId': return isBankDetailsRequired() && (!value || value === '') ? 'Bank is required' : '';
      case 'chequeDetails': return isBankDetailsRequired() && (!value || value.toString().trim() === '') ? 'Transaction Details is required' : '';
      default: return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    ['BillNumber', 'BillDate', 'MasterId', 'Amount'].forEach(f => {
      const err = validateField(f, formData[f]);
      if (err) newErrors[f] = err;
    });
    if (isBankDetailsRequired()) {
      ['ChequeNumber', 'ChequeDate', 'BankId', 'chequeDetails'].forEach(f => {
        const err = validateField(f, formData[f]);
        if (err) newErrors[f] = err;
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const handleSaveWithValidation = () => {
    setTouched({ BillNumber: true, BillDate: true, MasterId: true, Amount: true, BankId: true, ChequeNumber: true, ChequeDate: true, chequeDetails: true });
    const tc = getTypeBasedContent();
    const msgs = [];
    if (!formData.BillNumber || formData.BillNumber.toString().trim() === '') msgs.push(`${tc.billNoLabel.replace(' *', '')} is required`);
    if (!formData.BillDate) msgs.push(`${tc.billDateLabel.replace(' *', '')} is required`);
    if (!formData.MasterId) msgs.push('Master is required');
    if (!formData.Amount || parseFloat(formData.Amount) <= 0) msgs.push('Amount must be greater than 0');
    if (isBankDetailsRequired()) {
      if (!formData.ChequeNumber || formData.ChequeNumber.toString().trim() === '') msgs.push('Reference Number is required');
      if (!formData.ChequeDate) msgs.push('Transaction Date is required');
      if (!formData.BankId || formData.BankId === '') msgs.push('Bank is required');
      if (!formData.chequeDetails || formData.chequeDetails.toString().trim() === '') msgs.push('Transaction Details is required');
    }
    if (msgs.length > 0) { showError(msgs.join(' | ')); validateForm(); return; }
    if (validateForm()) onSave();
  };

  const handleChange = (field, value) => {
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (!isViewMode) setHasUnsavedChanges(true);

    if (field === 'MasterId') {
      const selectedMaster = masters.find(m => m.Id === parseInt(value));
      setPaymentReceiptDetails([]);
      lastFetchedMasterIdRef.current = value ? parseInt(value) : null;
      if (selectedMaster && fetchBillsForMaster) {
        try { fetchBillsForMaster(parseInt(value), formData.IsType); } catch (e) { console.error(e); }
      }
      setFormData(prev => ({ ...prev, [field]: value, MasterName: selectedMaster?.Name || '', IsSaleAndPurchase: !!selectedMaster, Amount: 0, TotalAmount: 0, RoundOff: 0 }));
    } else if (field === 'PaymentType') {
      const cashMethod = paymentMethods && paymentMethods.length > 0
        ? paymentMethods.find(pm => pm.Name && pm.Name.toLowerCase().includes('cash'))
        : null;
      const cashId = cashMethod ? cashMethod.Id : 1;
      const chequeId = getChequePaymentMethodId();
      if (parseInt(value) === cashId) {
        setErrors(prev => ({ ...prev, BankId: '' }));
        setFormData(prev => ({ ...prev, [field]: parseInt(value), ChequeNumber: '', ChequeDate: '', BankId: null, chequeDetails: '', ChequeStatusId: 1 }));
      } else if (parseInt(value) === chequeId) setFormData(prev => ({ ...prev, [field]: parseInt(value), ChequeStatusId: 2 }));
      else setFormData(prev => ({ ...prev, [field]: parseInt(value) }));
    } else if (field === 'IsType') {
      const isPayment = value === true || value === 'payment';
      setFormData(prev => ({ ...prev, [field]: isPayment, Amount: 0, TotalAmount: 0, RoundOff: 0 }));
      setPaymentReceiptDetails([]);
      if (formData.MasterId && fetchBillsForMaster) {
        lastFetchedMasterIdRef.current = parseInt(formData.MasterId);
        fetchBillsForMaster(parseInt(formData.MasterId), isPayment);
      }
      if (fetchMaxBillNo && modalAction === 'create') {
        fetchMaxBillNo(isPayment).then(d => {
          const bn = d.billPrefix && d.finYear ? `${d.billPrefix}/${d.billNumber}/${d.finYear}` : d.billNumber.toString();
          setFormData(prev => ({ ...prev, BillNumber: bn }));
        }).catch(console.error);
      }
    } else if (field === 'IsGST') {
      const isGST = value === true || value === 'true';
      if (isGST) setFormData(prev => ({ ...prev, [field]: isGST }));
      else { setFormData(prev => ({ ...prev, [field]: isGST, IsIGST: false, GSTRate: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0 })); calculateTotals(); }
    } else if (field === 'IsIGST') {
      const isIGST = value === true || value === 'true';
      setFormData(prev => ({ ...prev, [field]: isIGST }));
      if (!formData.IsGST) { setFormData(prev => ({ ...prev, [field]: false, GSTRate: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0 })); calculateTotals(); }
    } else if (field === 'Amount' || field === 'GSTRate') {
      setFormData(prev => ({ ...prev, [field]: value }));
      if (field === 'Amount') {
        distributeAmountAcrossBills(value);
        if (!formData.IsGST) {
          const amt = parseFloat(value) || 0;
          const roundOff = Math.round(amt) - amt;
          setFormData(prev => ({ ...prev, [field]: value, RoundOff: Math.round(roundOff * 100) / 100, TotalAmount: Math.round(amt) }));
        }
      }
    } else if (field === 'BankId') {
      const bankIdVal = value !== '' && value != null ? parseInt(value) : null;
      setFormData(prev => ({ ...prev, [field]: bankIdVal }));
    } else if (field === 'ChequeStatusId') {
      setFormData(prev => ({ ...prev, [field]: parseInt(value) }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleDetailChange = (index, field, value) => {
    const updatedDetails = [...paymentReceiptDetails];
    if (field === 'IsSelected') {
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      updatedDetails[index].PaymentAmount = value ? (updatedDetails[index].PendingAmount || 0) : 0;
    } else if (field === 'PaymentAmount') {
      const numValue = parseFloat(value) || 0;
      if (numValue > (updatedDetails[index].PendingAmount || 0)) { showError(`Amount cannot exceed pending amount of ${formatCurrency(updatedDetails[index].PendingAmount)}`); return; }
      updatedDetails[index] = { ...updatedDetails[index], [field]: numValue, IsSelected: numValue > 0 };
    }
    setPaymentReceiptDetails(updatedDetails);
    const total = updatedDetails.filter(d => d.IsSelected && d.PaymentAmount > 0).reduce((sum, d) => sum + (parseFloat(d.PaymentAmount) || 0), 0);
    // Mark that amount is being updated from bill selection, so useEffect won't redistribute
    amountChangedFromBillsRef.current = true;
    setFormData(prev => ({ ...prev, Amount: total, TotalAmount: total }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getSelectedMaster = () => !formData.MasterId ? null : masters.find(m => m.Id === parseInt(formData.MasterId));
  const selectedMaster = getSelectedMaster();
  const isSupplier = selectedMaster?.MasterType === 'Supplier';

  return (
    <>
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            if (hasUnsavedChanges && !isViewMode) { setCancelConfirmOpen(true); return; }
          }
          onClose();
        }}
        maxWidth="lg" fullWidth fullScreen={isMobile} disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, maxWidth: '1000px', position: 'relative' } }}
      >
        {/* Loading Overlay */}
        <Backdrop sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.9)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} open={modalLoading}>
          <CircularProgress size={40} sx={{ color: currentTheme.primary }} />
          <Typography variant="body2" sx={{ mt: 2, color: currentTheme.primary, fontWeight: 600 }}>Loading {transactionType === 'payment' ? 'Payment' : 'Receipt'}...</Typography>
        </Backdrop>

        <DialogTitle sx={{ py: 1, px: 2, borderBottom: `1px solid ${headerBorderColor}`, bgcolor: headerColor }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Payment sx={{ color: iconColor, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: headerTextColor, fontSize: isMobile ? '0.9rem' : '1rem', fontFamily: 'Times New Roman, serif' }}>
                {typeContent.title}
              </Typography>
            </Box>
            <IconButton onClick={handleCancelClick} size="small" sx={{ p: 0.5, color: iconColor }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1 : 1.5, bgcolor: contentBg }}>
          {/* Section: Payment/Receipt Info */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : currentTheme.primary, display: 'block', mb: 0.5 }}>
            {typeContent.infoCaption}
          </Typography>

          <Box sx={{ bgcolor: sectionBg, p: 1, borderRadius: 1, mb: 1 }}>
            <Grid container spacing={0.75}>
              <Grid item xs={6} sm={1.5}>
                <TextField fullWidth size="small" label={typeContent.billNoLabel}
                  value={formData.BillNumber || ''} onChange={(e) => handleChange('BillNumber', e.target.value)}
                  onBlur={() => handleBlur('BillNumber')} disabled={isViewMode}
                  error={touched.BillNumber && !!errors.BillNumber} helperText={touched.BillNumber && errors.BillNumber}
                  sx={compactInput} />
              </Grid>
              <Grid item xs={6} sm={2}>
                {isViewMode ? (
                  <TextField fullWidth size="small" label={typeContent.billDateLabel}
                    value={formatDate(formData.BillDate) || '-'} disabled
                    InputLabelProps={{ shrink: true }} sx={compactInput} />
                ) : (
                  <TextField fullWidth size="small" type="date" label={typeContent.billDateLabel}
                    value={formData.BillDate || ''} onChange={(e) => handleChange('BillDate', e.target.value)}
                    onBlur={() => handleBlur('BillDate')}
                    error={touched.BillDate && !!errors.BillDate} helperText={touched.BillDate && errors.BillDate}
                    InputLabelProps={{ shrink: true }} sx={compactInput} />
                )}
              </Grid>
              <Grid item xs={12} sm={5}>
                {isViewMode ? (
                  <TextField fullWidth size="small" label="Master" value={selectedMaster?.Name || '-'} disabled sx={compactInput} />
                ) : (
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={masters}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option?.Name || '';
                    }}
                    value={masters.find(m => m.Id === parseInt(formData.MasterId)) || null}
                    onChange={(event, newValue) => {
                      if (newValue && typeof newValue === 'object') {
                        handleChange('MasterId', newValue.Id);
                      } else {
                        handleChange('MasterId', '');
                      }
                    }}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const filterText = inputValue.toLowerCase();
                      return options.filter(option =>
                        (option.Name || '').toLowerCase().includes(filterText)
                      );
                    }}
                    isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.Id}>
                        {option.Name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Master *"
                        error={touched.MasterId && !!errors.MasterId}
                        helperText={touched.MasterId && errors.MasterId}
                        onBlur={() => handleBlur('MasterId')}
                        sx={{
                          '& .MuiInputBase-root': { height: '28px', minHeight: '28px', padding: '0 9px !important' },
                          '& .MuiInputBase-input': { padding: '0 !important', fontSize: '0.75rem', height: '28px' },
                          '& .MuiInputLabel-root': { fontSize: '0.75rem', lineHeight: '1rem' },
                          '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' }
                        }}
                      />
                    )}
                    ListboxProps={{ style: { maxHeight: 200, fontSize: '0.75rem' } }}
                    sx={{ width: '100%' }}
                  />
                )}
              </Grid>
              <Grid item xs={6} sm={1.5}>
                <FormControl fullWidth size="small" sx={compactInput}>
                  <InputLabel>Type *</InputLabel>
                  <Select value={formData.IsType ? 'payment' : 'receipt'} onChange={(e) => handleChange('IsType', e.target.value === 'payment')} label="Type *" disabled={isViewMode}
                    MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem' } } } }}>
                    <MenuItem value="payment" sx={{ color: themeConfig.payment.primary, fontWeight: 600 }}>Payment</MenuItem>
                    <MenuItem value="receipt" sx={{ color: themeConfig.receipt.primary, fontWeight: 600 }}>Receipt</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small" sx={compactInput}>
                  <InputLabel>Payment Method</InputLabel>
                  <Select value={formData.PaymentType || 1} onChange={(e) => handleChange('PaymentType', e.target.value)} label="Payment Method" disabled={isViewMode}
                    MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem' } } } }}>
                    {paymentMethods && paymentMethods.length > 0
                      ? paymentMethods.filter(pm => pm.IsActive !== false).map(pm => <MenuItem key={pm.Id} value={pm.Id}>{pm.Name}</MenuItem>)
                      : [<MenuItem key={1} value={1}>Cash</MenuItem>, <MenuItem key={2} value={2}>Cheque</MenuItem>, <MenuItem key={3} value={3}>Online Transfer</MenuItem>]}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Bank Details */}
          {formData.PaymentType !== 1 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : currentTheme.primary, display: 'block', mb: 0.5 }}>Bank Details</Typography>
              <Box sx={{ bgcolor: sectionBg, p: 1, borderRadius: 1, mb: 1 }}>
                <Grid container spacing={0.75}>
                  <Grid item xs={6} sm={2.4}>
                    <TextField fullWidth size="small" label="Reference Number *"
                      value={formData.ChequeNumber || ''}
                      onChange={(e) => handleChange('ChequeNumber', e.target.value)}
                      onBlur={() => handleBlur('ChequeNumber')}
                      disabled={isViewMode}
                      error={touched.ChequeNumber && !!errors.ChequeNumber}
                      helperText={touched.ChequeNumber && errors.ChequeNumber}
                      sx={compactInput} />
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    {isViewMode ? (
                      <TextField fullWidth size="small" label="Transaction Date"
                        value={formatDate(formData.ChequeDate) || '-'} disabled
                        InputLabelProps={{ shrink: true }} sx={compactInput} />
                    ) : (
                      <TextField fullWidth size="small" type="date" label="Transaction Date *"
                        value={formData.ChequeDate || ''} onChange={(e) => handleChange('ChequeDate', e.target.value)}
                        onBlur={() => handleBlur('ChequeDate')}
                        error={touched.ChequeDate && !!errors.ChequeDate}
                        helperText={touched.ChequeDate && errors.ChequeDate}
                        InputLabelProps={{ shrink: true }} sx={compactInput} />
                    )}
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <FormControl fullWidth size="small" sx={compactInput} error={touched.BankId && !!errors.BankId}>
                      <InputLabel>Bank *</InputLabel>
                      <Select
                        value={formData.BankId || ''}
                        onChange={(e) => handleChange('BankId', e.target.value)}
                        onBlur={() => handleBlur('BankId')}
                        label="Bank *"
                        disabled={isViewMode}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.75rem' } } } }}
                      >
                        <MenuItem value=""><em>Select Bank</em></MenuItem>
                        {banks && banks.length > 0 ? (
                          banks.map(b => (
                            <MenuItem key={b.MasterId || b.Id} value={b.MasterId || b.Id}>
                              {b.Name || b.MasterName}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="" disabled>No banks available</MenuItem>
                        )}
                      </Select>
                      {touched.BankId && errors.BankId && (
                        <FormHelperText>{errors.BankId}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <TextField fullWidth size="small" label="Transaction Details *"
                      value={formData.chequeDetails || ''}
                      onChange={(e) => handleChange('chequeDetails', e.target.value)}
                      onBlur={() => handleBlur('chequeDetails')}
                      disabled={isViewMode}
                      error={touched.chequeDetails && !!errors.chequeDetails}
                      helperText={touched.chequeDetails && errors.chequeDetails}
                      sx={compactInput} />
                  </Grid>
                  {formData.PaymentType === chequePaymentMethodId && (
                    <Grid item xs={12} sm={2.4}>
                      <FormControl fullWidth size="small" sx={compactInput}>
                        <InputLabel>Cheque Status</InputLabel>
                        <Select value={formData.ChequeStatusId || 1} onChange={(e) => handleChange('ChequeStatusId', e.target.value)} label="Cheque Status" disabled={isViewMode}
                          MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem' } } } }}>
                          <MenuItem value={1}>Pending</MenuItem>
                          <MenuItem value={2}>Passed</MenuItem>
                          <MenuItem value={3}>Bounced</MenuItem>
                          <MenuItem value={4}>Cancelled</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </>
          )}

          {/* Amount & GST */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : currentTheme.primary, display: 'block', mb: 0.5 }}>Amount & GST Details</Typography>
          <Box sx={{ bgcolor: sectionBg, p: 1, borderRadius: 1, mb: 1 }}>
            <Grid container spacing={0.75}>
              <Grid item xs={12} sm={2}>
                <TextField fullWidth size="small" type="number" label="Amount *"
                  value={formData.Amount || ''} onChange={(e) => handleChange('Amount', e.target.value)}
                  onBlur={() => handleBlur('Amount')} disabled={isViewMode}
                  error={touched.Amount && !!errors.Amount} helperText={touched.Amount && errors.Amount}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ ...compactInput, '& .MuiInputBase-root': { height: '28px' }, '& .MuiInputBase-input': { textAlign: 'right', py: 0, fontSize: '0.75rem' } }} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <Box sx={{ height: '28px', display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: 1, bgcolor: '#fff', px: 1 }}>
                  <FormControlLabel control={<Checkbox checked={formData.IsGST || false} onChange={(e) => handleChange('IsGST', e.target.checked)} disabled={isViewMode} size="small" sx={{ p: '2px', '& .MuiSvgIcon-root': { fontSize: 16 } }} />}
                    label={<Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>GST Applicable</Typography>}
                    sx={{ margin: 0 }} />
                </Box>
              </Grid>
              {formData.IsGST && (
                <>
                  <Grid item xs={6} sm={2}>
                    <Box sx={{ height: '28px', display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: 1, bgcolor: '#fff', px: 1 }}>
                      <FormControlLabel control={<Checkbox checked={formData.IsIGST || false} onChange={(e) => handleChange('IsIGST', e.target.checked)} disabled={isViewMode} size="small" sx={{ p: '2px', '& .MuiSvgIcon-root': { fontSize: 16 } }} />}
                        label={<Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>IGST Applicable</Typography>}
                        sx={{ margin: 0 }} />
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <FormControl fullWidth size="small" sx={compactInput}>
                      <InputLabel>Tax %</InputLabel>
                      <Select value={formData.GSTRate || ''} onChange={(e) => handleChange('GSTRate', e.target.value)} label="Tax %" disabled={isViewMode}
                        MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem' } } } }}>
                        <MenuItem value=""><em>Select Tax Rate</em></MenuItem>
                        {gstRates && gstRates.length > 0
                          ? gstRates.filter(r => r.IsActive !== false).map(r => <MenuItem key={r.Id} value={r.GSTRate}>{r.GSTRate}%</MenuItem>)
                          : [0, 5, 12, 18, 28].map(v => <MenuItem key={v} value={v}>{v}%</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  {!formData.IsIGST && (
                    <>
                      <Grid item xs={6} sm={2}>
                        <TextField fullWidth size="small" label="CGST Amount" value={formData.CGSTAmount || 0} disabled sx={{ ...compactInput, '& .MuiInputBase-input': { textAlign: 'right', py: 0, fontSize: '0.75rem' } }} />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField fullWidth size="small" label="SGST Amount" value={formData.SGSTAmount || 0} disabled sx={{ ...compactInput, '& .MuiInputBase-input': { textAlign: 'right', py: 0, fontSize: '0.75rem' } }} />
                      </Grid>
                    </>
                  )}
                  {formData.IsIGST && (
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth size="small" label="IGST Amount" value={formData.IGSTAmount || 0} disabled sx={{ ...compactInput, '& .MuiInputBase-input': { textAlign: 'right', py: 0, fontSize: '0.75rem' } }} />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Box>

          {/* Party & Remarks */}
          <Box sx={{ bgcolor: sectionBg, p: 1, borderRadius: 1, mb: 1 }}>
            <Grid container spacing={0.75}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small" sx={compactInput}>
                  <InputLabel>Party</InputLabel>
                  <Select value={formData.PartyId || ''} onChange={(e) => handleChange('PartyId', e.target.value)} disabled={isViewMode}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.75rem' } } } }}>
                    <MenuItem value=""><em>Select Party</em></MenuItem>
                    {masters.filter(m => m.MasterTypeId === 1).map(m => <MenuItem key={m.Id} value={m.Id}>{m.Name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={9}>
                <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''} onChange={(e) => handleChange('Remarks', e.target.value)} disabled={isViewMode}
                  sx={compactInput} />
              </Grid>
            </Grid>
          </Box>

          {/* Bills Table */}
          {formData.MasterId && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : currentTheme.primary }}>
                  {formData.IsType ? 'Purchase Bills' : 'Sale Bills'}
                </Typography>
                {paymentReceiptDetails.length > 0 && !paymentReceiptDetails[0]?.noData && (
                  <Chip label={`${paymentReceiptDetails.length} Bills`} size="small" sx={{ bgcolor: isViewMode ? '#e2e8f0' : (transactionType === 'payment' ? '#fecaca' : '#d1fae5'), color: isViewMode ? '#475569' : currentTheme.primary, fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
                )}
              </Box>

              <Box sx={{ border: `1px solid ${isViewMode ? '#e2e8f0' : headerBorderColor}`, borderRadius: 1, mb: 1.5 }}>
                {paymentReceiptDetails.length === 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: currentTheme.primary, mb: 1.5 }} />
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', fontWeight: 500 }}>Loading {isSupplier ? 'purchase' : 'sale'} data...</Typography>
                  </Box>
                )}
                {paymentReceiptDetails.length === 1 && paymentReceiptDetails[0]?.noData && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <Warning sx={{ color: '#fbbf24', fontSize: 36, mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', fontWeight: 500 }}>No {formData.IsType ? 'purchase bills' : 'sale bills'} found</Typography>
                  </Box>
                )}
                {paymentReceiptDetails.length > 0 && !paymentReceiptDetails[0]?.noData && (
                  <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr style={{ background: isViewMode ? 'linear-gradient(135deg, #334155 0%, #1e293b 100%)' : 'linear-gradient(135deg, #2C3E6E 0%, #1a2a4a 100%)' }}>
                          {!isViewMode && <th style={{ padding: '4px 6px', textAlign: 'center', color: 'white', fontWeight: 600, width: '6%' }}>Select</th>}
                          <th style={{ padding: '4px 6px', textAlign: 'left', color: 'white', fontWeight: 600, width: '28%' }}>Bill Details</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', color: 'white', fontWeight: 600, width: '16%' }}>Bill Amount</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', color: 'white', fontWeight: 600, width: '16%' }}>Paid Amount</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', color: 'white', fontWeight: 600, width: '16%' }}>Pending Amount</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', color: 'white', fontWeight: 600, width: '16%' }}>{formData.IsType ? 'Payment Amount' : 'Receipt Amount'}</th>
                          <th style={{ padding: '4px 6px', textAlign: 'center', color: 'white', fontWeight: 600, width: '10%' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentReceiptDetails.map((detail, index) => (
                          <tr key={detail.Id} style={{ backgroundColor: index % 2 === 0 ? (isViewMode ? '#f1f5f9' : '#f8f9fa') : '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                            {!isViewMode && (
                              <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                                <Checkbox checked={detail.IsSelected || false} onChange={(e) => handleDetailChange(index, 'IsSelected', e.target.checked)} disabled={isViewMode} size="small"
                                  sx={{ p: '2px', color: '#666', '&.Mui-checked': { color: currentTheme.primary }, '& .MuiSvgIcon-root': { fontSize: 16 } }} />
                              </td>
                            )}
                            <td style={{ padding: '4px 6px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.7rem', color: '#333', lineHeight: 1.3 }}>{detail.Description}</Typography>
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 500, color: '#333' }}>{formatCurrency(detail.Amount)}</Typography>
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 500, color: themeConfig.receipt.primary }}>{formatCurrency(detail.PaidAmount || 0)}</Typography>
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 500, color: themeConfig.payment.primary }}>{formatCurrency(detail.PendingAmount)}</Typography>
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                              {isViewMode ? (
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: currentTheme.primary }}>{formatCurrency(detail.PaymentAmount || 0)}</Typography>
                              ) : (
                                <TextField size="small" type="number" value={detail.PaymentAmount || ''} onChange={(e) => handleDetailChange(index, 'PaymentAmount', e.target.value)}
                                  disabled={!detail.IsSelected} inputProps={{ min: 0, max: detail.PendingAmount, step: 0.01, style: { textAlign: 'right', fontSize: '0.7rem', padding: '3px' } }}
                                  sx={{ width: '100%', '& .MuiOutlinedInput-root': { height: '24px' } }} />
                              )}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                              <Chip label={detail.PendingAmount > 0 ? 'Pending' : 'Paid'} size="small"
                                sx={{ fontSize: '0.65rem', fontWeight: 600, height: 18, borderRadius: '9px',
                                  ...(detail.PendingAmount > 0 ? { bgcolor: '#fff3cd', color: '#856404' } : { bgcolor: '#d4edda', color: '#155724' }) }} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                )}
              </Box>
            </>
          )}

          {!formData.MasterId && (
            <Box sx={{ border: '1px dashed #ccc', borderRadius: 1, p: 3, textAlign: 'center', bgcolor: '#f9fafb' }}>
              <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', fontWeight: 500 }}>Select a master to view related transactions</Typography>
              <Typography variant="caption" sx={{ color: '#999', fontSize: '0.75rem' }}>Choose a master to see {formData.IsType ? 'purchase bills' : 'sale bills'}</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 1.5, py: 1, borderTop: actionsBorder, bgcolor: actionsBg, justifyContent: 'space-between' }}>
          <Box>
            {(modalAction === 'view' || modalAction === 'edit') && formData.Id && onPrint && (
              <Button onClick={() => onPrint(formData.Id)} variant="contained" size="small" startIcon={<Print />}
                sx={{ bgcolor: currentTheme.primary, '&:hover': { bgcolor: currentTheme.secondary }, textTransform: 'none', fontSize: '0.8rem' }}>
                Download PDF
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCancelClick} variant="outlined" size="small" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>Cancel</Button>
            {!isViewMode && (
              <Button onClick={handleSaveWithValidation} variant="contained" size="small" disabled={loading}
                sx={{ bgcolor: currentTheme.primary, '&:hover': { bgcolor: currentTheme.secondary }, textTransform: 'none', fontSize: '0.8rem' }}>
                {loading ? <><CircularProgress size={12} sx={{ color: 'white', mr: 0.5 }} />Saving...</> : 'Save'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelConfirmOpen} onClose={handleStayOnPage} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: '#fef3c7', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>Unsaved Changes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem', mb: 0.5 }}>You have unsaved changes that will be lost if you leave.</Typography>
          <Typography sx={{ color: '#dc6363', fontSize: '0.75rem', fontWeight: 600 }}>Are you sure you want to exit without saving?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={handleStayOnPage} variant="outlined" size="small" sx={{ textTransform: 'none' }}>Stay on Page</Button>
          <Button onClick={handleConfirmCancel} variant="contained" size="small" sx={{ bgcolor: '#dc6363', '&:hover': { bgcolor: '#b91c1c' }, textTransform: 'none' }}>Exit Without Saving</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default PaymentReceiptsModal;

